/**
 * React Hook — useCouncilStore
 *
 * Bridges the vanilla TypeScript councilStore into React.
 * Same pattern as useSimulationStore.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { councilStore } from '../store/councilStore';
import type { CouncilState, CouncilAnalysisRequest } from '../types/council';

export interface UseCouncilStore {
  // State
  status: CouncilState['status'];
  currentAnalysis: CouncilState['currentAnalysis'];
  history: CouncilState['history'];
  error: CouncilState['error'];
  errorType: CouncilState['errorType'];
  retryCount: CouncilState['retryCount'];

  // Derived
  isIdle: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  isRetrying: boolean;
  hasResults: boolean;

  // Actions
  runAnalysis: (request: CouncilAnalysisRequest) => Promise<void>;
  resetAnalysis: () => void;
}

export function useCouncilStore(): UseCouncilStore {
  const [state, setState] = useState<CouncilState>(councilStore.getState());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const unsubscribe = councilStore.subscribe((newState) => {
      if (mountedRef.current) {
        setState(newState);
      }
    });

    // Sync on mount
    setState(councilStore.getState());

    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, []);

  const runAnalysis = useCallback(async (request: CouncilAnalysisRequest) => {
    await councilStore.runAnalysis(request);
  }, []);

  const resetAnalysis = useCallback(() => {
    councilStore.resetAnalysis();
  }, []);

  return {
    // State
    status: state.status,
    currentAnalysis: state.currentAnalysis,
    history: state.history,
    error: state.error,
    errorType: state.errorType,
    retryCount: state.retryCount,

    // Derived
    isIdle: state.status === 'idle',
    isLoading: state.status === 'loading',
    isSuccess: state.status === 'success',
    isError: state.status === 'error',
    isRetrying: state.status === 'retrying',
    hasResults: state.currentAnalysis !== null,

    // Actions
    runAnalysis,
    resetAnalysis,
  };
}
